<template>
  <div class="max-w-4xl mx-auto space-y-3 py-3 mb-24">
    <!-- Preset Selector -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Transcript PDF Template</h2>
      <p class="text-sm text-gray-500 mb-2">Choose a preset or customize how transcript PDFs look when generated.</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div
          v-for="preset in presets"
          :key="preset.key"
          class="border rounded-lg p-2.5 cursor-pointer transition-all hover:shadow-md"
          :class="config.preset === preset.key ? 'border-primary bg-primary/10 ring-2 ring-primary/20' : 'border-gray-200'"
          @click="applyPreset(preset)"
        >
          <div class="flex items-center justify-between mb-1">
            <span class="font-semibold text-sm text-gray-800">{{ preset.name }}</span>
            <i v-if="config.preset === preset.key" class="pi pi-check-circle text-primary"></i>
          </div>
          <p class="text-xs text-gray-500">{{ preset.description }}</p>
        </div>
      </div>
    </div>

    <!-- Header / Logo -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Header & Logo</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Logo Position</label>
          <Select
            v-model="config.logo_position"
            :options="logoPositionOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Logo Height (px)</label>
          <Slider v-model="config.logo_height" :min="20" :max="150" class="w-full mt-3" />
          <span class="text-xs text-gray-400">{{ config.logo_height }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Report Title</label>
          <InputText v-model="config.report_title" class="w-full" placeholder="ACADEMIC TRANSCRIPT" :invalid="!!fieldError('report_title')" />
          <div class="flex justify-between gap-2 mt-1">
            <small class="text-red-500">{{ fieldError('report_title') }}</small>
            <small class="text-gray-400">{{ textLength('report_title') }}</small>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Report Subtitle</label>
          <InputText v-model="config.report_subtitle" class="w-full" placeholder="Office of the Registrar" :invalid="!!fieldError('report_subtitle')" />
          <div class="flex justify-between gap-2 mt-1">
            <small class="text-red-500">{{ fieldError('report_subtitle') }}</small>
            <small class="text-gray-400">{{ textLength('report_subtitle') }}</small>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap gap-3 mt-2">
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_school_name" />
          <span class="text-sm text-gray-600">Show school name</span>
        </div>
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_school_address" />
          <span class="text-sm text-gray-600">Show school address</span>
        </div>
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_report_title" />
          <span class="text-sm text-gray-600">Show report title</span>
        </div>
      </div>
    </div>

    <!-- Table / Font Settings -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Table & Font</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Font</label>
          <Select
            v-model="config.font_family"
            :options="fontFamilies"
            optionLabel="label"
            optionValue="key"
            class="w-full"
            :invalid="!!fieldError('font_family')"
          />
          <small class="text-red-500">{{ fieldError('font_family') }}</small>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Body Font Size</label>
          <Slider v-model="config.font_size" :min="6" :max="16" class="w-full mt-3" />
          <span class="text-xs text-gray-400">{{ config.font_size }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Header Font Size</label>
          <Slider v-model="config.header_font_size" :min="10" :max="30" class="w-full mt-3" />
          <span class="text-xs text-gray-400">{{ config.header_font_size }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Table Header Font Size</label>
          <Slider v-model="config.table_header_font_size" :min="6" :max="14" class="w-full mt-3" />
          <span class="text-xs text-gray-400">{{ config.table_header_font_size }}px</span>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Row Padding</label>
          <Slider v-model="config.row_padding" :min="1" :max="12" class="w-full mt-3" />
          <span class="text-xs text-gray-400">{{ config.row_padding }}px</span>
        </div>
      </div>
      <div class="flex flex-wrap gap-3 mt-2">
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.show_borders" />
          <span class="text-sm text-gray-600">Show table borders</span>
        </div>
        <div class="flex items-center gap-2">
          <InputSwitch v-model="config.striped_rows" />
          <span class="text-sm text-gray-600">Striped rows</span>
        </div>
      </div>
    </div>

    <!-- Page Layout -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Page Layout</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Page Size</label>
          <Select
            v-model="config.page_size"
            :options="pageSizeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Orientation</label>
          <Select
            v-model="config.page_orientation"
            :options="orientationOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Page Margin</label>
          <Slider v-model="config.page_margin" :min="0" :max="40" class="w-full mt-3" />
          <span class="text-xs text-gray-400">{{ config.page_margin }}mm</span>
        </div>
      </div>
    </div>

    <!-- Student Information Fields -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Student Information</h2>
      <p class="text-sm text-gray-500 mb-2">Choose which student details to display on the transcript.</p>

      <div class="flex items-center gap-2 mb-3">
        <InputSwitch v-model="config.show_student_info" />
        <span class="text-sm font-medium text-gray-600">Show student information section</span>
      </div>

      <div v-if="config.show_student_info" class="space-y-2">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1">Layout</label>
          <Select
            v-model="config.student_info_layout"
            :options="studentInfoLayoutOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full md:w-64"
          />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          <div
            v-for="field in availableStudentFields"
            :key="field.key"
            class="flex items-center justify-between"
          >
            <span class="text-sm text-gray-600">{{ field.label }}</span>
            <InputSwitch
              :modelValue="isStudentFieldEnabled(field.key)"
              @update:modelValue="toggleStudentField(field.key, $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Section Visibility -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Sections</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">Cover Letter</span>
            <p class="text-xs text-gray-400">A covering page addressed to the recipient, printed before the results.</p>
          </div>
          <InputSwitch v-model="config.show_cover_letter" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">Semester Summary</span>
            <p class="text-xs text-gray-400">Units and GPA at the end of each semester.</p>
          </div>
          <InputSwitch v-model="config.show_semester_summary" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">Cumulative Record</span>
            <p class="text-xs text-gray-400">Running totals and CGPA across all semesters.</p>
          </div>
          <InputSwitch v-model="config.show_cumulative" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">Partial Transcript Footnote</span>
            <p class="text-xs text-gray-400">A note saying the transcript does not yet cover the full programme.</p>
          </div>
          <InputSwitch v-model="config.show_partial_footnote" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">E-Stamp</span>
            <p class="text-xs text-gray-400">The school's digital stamp, printed near the signatures.</p>
          </div>
          <InputSwitch v-model="config.show_estamp" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">Footer</span>
            <p class="text-xs text-gray-400">A line of text at the bottom of every page.</p>
          </div>
          <InputSwitch v-model="config.show_footer" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">Signature Lines</span>
            <p class="text-xs text-gray-400">Blank lines for officials to sign, labelled below.</p>
          </div>
          <InputSwitch v-model="config.show_signature_lines" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <span class="text-sm text-gray-600">Watermark</span>
            <p class="text-xs text-gray-400">Faint text printed diagonally across each page.</p>
          </div>
          <InputSwitch v-model="config.show_watermark" />
        </div>
      </div>

      <!-- Conditional fields -->
      <div class="mt-2 space-y-3">
        <div v-if="config.show_footer">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Custom Footer Text
            <span class="text-xs text-gray-400">(leave blank to show the date the transcript was generated)</span>
          </label>
          <InputText
            v-model="config.footer_text"
            class="w-full"
            placeholder="e.g. Not valid without the seal and the Registrar's signature."
            :invalid="!!fieldError('footer_text')"
          />
          <div class="flex justify-between gap-2 mt-1">
            <small class="text-red-500">{{ fieldError('footer_text') }}</small>
            <small class="text-gray-400">{{ textLength('footer_text') }}</small>
          </div>
        </div>
        <div v-if="config.show_watermark">
          <label class="block text-sm font-medium text-gray-600 mb-1">Watermark Text</label>
          <InputText v-model="config.watermark_text" class="w-full" placeholder="CONFIDENTIAL" :invalid="!!fieldError('watermark_text')" />
          <div class="flex justify-between gap-2 mt-1">
            <small class="text-red-500">{{ fieldError('watermark_text') }}</small>
            <small class="text-gray-400">{{ textLength('watermark_text') }}</small>
          </div>
        </div>
        <div v-if="config.show_signature_lines">
          <label class="block text-sm font-medium text-gray-600 mb-1">
            Signature Labels
            <span class="text-xs text-gray-400">(press Enter after each one, up to 4 — e.g. Registrar, Dean)</span>
          </label>
          <AutoComplete
            v-model="config.signature_labels"
            multiple
            :typeahead="false"
            class="w-full"
            :invalid="!!fieldError('signature_labels')"
          />
          <small class="text-red-500">{{ fieldError('signature_labels') }}</small>
        </div>
      </div>
    </div>

    <!-- Save Bar -->
    <div class="flex items-center justify-end gap-2 sticky bottom-4">
      <span v-if="isDirty" class="text-xs text-gray-500 bg-white rounded px-2 py-1 shadow-sm">You have unsaved changes</span>
      <Button
        v-if="isDirty"
        label="Discard"
        icon="pi pi-undo"
        severity="secondary"
        outlined
        size="small"
        :disabled="isSaving"
        @click="discardChanges"
      />
      <Button
        label="Save Configuration"
        icon="pi pi-check"
        class="!bg-primary !text-white shadow-lg"
        :loading="isSaving"
        :disabled="isSaving || !isDirty"
        size="small"
        @click="saveConfig"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';

import { usePdfTranscriptConfigForm } from '@/composables/transcript/usePdfTranscriptConfigForm';

const {
  config,
  presets,
  studentFields: availableStudentFields,
  fontFamilies,
  isSaving,
  isDirty,
  fieldError,
  textLength,
  applyPreset,
  isStudentFieldEnabled,
  toggleStudentField,
  discardChanges,
  saveConfig,
} = usePdfTranscriptConfigForm();

const logoPositionOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const pageSizeOptions = [
  { label: 'A4', value: 'A4' },
  { label: 'Letter', value: 'Letter' },
];

const orientationOptions = [
  { label: 'Portrait', value: 'portrait' },
  { label: 'Landscape', value: 'landscape' },
];

const studentInfoLayoutOptions = [
  { label: 'Two columns', value: 'two_column' },
  { label: 'Single column', value: 'single_column' },
];

// Warn before closing or reloading the tab with unsaved changes.
function warnUnsaved(event: BeforeUnloadEvent) {
  if (!isDirty.value) return;
  event.preventDefault();
  event.returnValue = '';
}
onMounted(() => window.addEventListener('beforeunload', warnUnsaved));
onBeforeUnmount(() => window.removeEventListener('beforeunload', warnUnsaved));
</script>
